import type { User } from "@/lib/types";
import { profileLocationUsableForNearMe } from "@/lib/notifications/homepage-near-me-quick-opt-in";
import type { NotificationSetupBannerMode } from "@/lib/notifications/notification-setup-eligibility";

export type MarketLocalBannerKind = "welcome" | "push_reminder" | null;

export type ResolveMarketNotificationSurfaceInput = {
  marketIntroOpen: boolean;
  globalSetupBannerVisible: boolean;
  globalSetupBannerMode: NotificationSetupBannerMode;
  pushReminderEligible: boolean;
};

export type ResolveMarketNotificationSurfaceResult = {
  localBanner: MarketLocalBannerKind;
  suppressMarketPushReminder: boolean;
};

/** Hide market push card when the app-shell setup strip already covers push. */
export function shouldSuppressMarketPushReminder(input: {
  globalSetupBannerVisible: boolean;
  globalSetupBannerMode: NotificationSetupBannerMode;
}): boolean {
  if (!input.globalSetupBannerVisible) {
    return false;
  }
  return (
    input.globalSetupBannerMode === "push" ||
    input.globalSetupBannerMode === "both"
  );
}

/**
 * Picks at most one full-width local banner on `/market` (below the global setup strip).
 * Priority: first-visit welcome → push reminder fallback.
 */
export function resolveMarketNotificationSurface(
  input: ResolveMarketNotificationSurfaceInput,
): ResolveMarketNotificationSurfaceResult {
  const suppressMarketPushReminder = shouldSuppressMarketPushReminder({
    globalSetupBannerVisible: input.globalSetupBannerVisible,
    globalSetupBannerMode: input.globalSetupBannerMode,
  });

  if (input.marketIntroOpen) {
    return {
      localBanner: "welcome",
      suppressMarketPushReminder,
    };
  }

  if (input.pushReminderEligible && !suppressMarketPushReminder) {
    return {
      localBanner: "push_reminder",
      suppressMarketPushReminder,
    };
  }

  return {
    localBanner: null,
    suppressMarketPushReminder,
  };
}

export function isMarketPushReminderEligible(input: {
  user: User | null;
  nearMePlatformEnabled: boolean;
  nearMeOn: boolean | null;
  browserSubscribed: boolean;
  pushPermission: NotificationPermission | "unsupported";
  pushSupported: boolean;
  pushVapidConfigured: boolean;
}): boolean {
  if (
    !input.user ||
    !input.nearMePlatformEnabled ||
    input.nearMeOn !== true ||
    input.browserSubscribed ||
    input.pushPermission === "denied" ||
    !input.pushSupported ||
    !input.pushVapidConfigured
  ) {
    return false;
  }

  return profileLocationUsableForNearMe({
    location: input.user.location,
    location_state_id: input.user.location_state_id,
    location_lga_id: input.user.location_lga_id,
    location_neighborhood_id: input.user.location_neighborhood_id,
  });
}
